import React from 'react';
import Icon from '../../../components/AppIcon';

const ValuesSection = () => {
  const coreValues = [
    {
      id: 1,
      icon: "Shield",
      title: "Trust & Transparency",
      description: "Building lasting relationships through honest communication and transparent processes.",
      principles: [
        "Open communication with all stakeholders",
        "Clear investment terms and expectations",
        "Regular portfolio company updates",
        "Ethical business practices"
      ],
      color: "from-blue-500 to-blue-600"
    },
    {
      id: 2,
      icon: "Zap",
      title: "Innovation & Excellence",
      description: "Pursuing cutting-edge solutions while maintaining the highest standards of quality.",
      principles: [
        "Embrace emerging technologies",
        "Continuous learning and adaptation",
        "Best-in-class operational support",
        "Data-driven decision making"
      ],
      color: "from-purple-500 to-purple-600"
    },
    {
      id: 3,
      icon: "Heart",
      title: "Partnership & Collaboration",
      description: "Working closely with entrepreneurs as true partners in their journey to success.",
      principles: [
        "Hands-on operational support",
        "Long-term relationship focus",
        "Collaborative problem solving",
        "Shared success mentality"
      ],
      color: "from-green-500 to-green-600"
    },
    {
      id: 4,
      icon: "Globe",
      title: "Global Vision & Local Expertise",
      description: "Combining European market knowledge with international scaling capabilities.",
      principles: [
        "Deep European market understanding",
        "International expansion support",
        "Cross-border partnerships",
        "Cultural sensitivity and adaptation"
      ],
      color: "from-orange-500 to-orange-600"
    }
  ];

  const culturalElements = [
    {
      icon: "Users",
      title: "Diverse Team",
      description: "Multicultural perspectives driving better investment decisions"
    },
    {
      icon: "Lightbulb",
      title: "Continuous Learning",
      description: "Regular training and industry knowledge sharing sessions"
    },
    {
      icon: "Coffee",
      title: "Work-Life Balance",
      description: "Flexible working arrangements and wellness programs"
    },
    {
      icon: "Award",
      title: "Recognition Culture",
      description: "Celebrating achievements and fostering professional growth"
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Icon name="Heart" size={20} className="text-white" />
            </div>
            <span className="text-primary font-semibold text-lg">Our Values</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Principles That
            <span className="text-gradient-brand block">Guide Our Success</span>
          </h2>
          
          <p className="text-xl text-muted-foreground leading-relaxed">
            Our core values shape every investment decision, partnership, and interaction. 
            They reflect our commitment to building sustainable, ethical, and impactful businesses.
          </p>
        </div>

        {/* Core Values Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {coreValues?.map((value, index) => (
            <div 
              key={value?.id}
              className={`group bg-card rounded-brand-lg overflow-hidden shadow-brand hover-lift transition-brand border border-border scroll-reveal stagger-${(index % 2) + 1}`}
            >
              {/* Header */}
              <div className={`bg-gradient-to-r ${value?.color} p-6 text-white`}>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-brand flex items-center justify-center">
                    <Icon name={value?.icon} size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{value?.title}</h3>
                    <p className="text-white/90 text-sm">{value?.description}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h4 className="font-semibold text-foreground mb-4">Key Principles:</h4>
                <ul className="space-y-3">
                  {value?.principles?.map((principle, principleIndex) => (
                    <li key={principleIndex} className="flex items-start space-x-3">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground text-sm leading-relaxed">{principle}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Office Culture */}
        <div className="bg-gradient-to-br from-muted/50 to-muted/30 rounded-brand-lg p-8 lg:p-12 border border-border">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-10 h-10 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Icon name="Building2" size={20} className="text-white" />
              </div>
              <span className="text-primary font-semibold text-lg">Office Culture</span>
            </div>
            
            <h3 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Hamburg Office Culture
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our Hamburg headquarters reflects the perfect blend of traditional European business 
              values and modern startup culture, creating an environment where innovation thrives.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {culturalElements?.map((element, index) => (
              <div 
                key={index}
                className={`bg-card rounded-brand p-6 text-center shadow-brand-sm hover-lift transition-brand scroll-reveal stagger-${(index % 4) + 1}`}
              >
                <div className="w-12 h-12 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-4">
                  <Icon name={element?.icon} size={20} className="text-white" />
                </div>
                <h4 className="font-semibold text-foreground mb-2">{element?.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{element?.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mt-20 text-center">
          <div className="max-w-4xl mx-auto bg-card rounded-brand-lg p-8 lg:p-12 shadow-brand border border-border">
            <div className="w-16 h-16 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-6">
              <Icon name="Target" size={28} className="text-white" />
            </div>
            
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-6">
              Our Commitment to Excellence
            </h3>
            
            <blockquote className="text-lg lg:text-xl text-muted-foreground leading-relaxed italic mb-8">
              "We are committed to being more than just investors. We are strategic partners, 
              mentors, and advocates for the entrepreneurs who dare to challenge the status quo 
              and build the digital solutions of tomorrow."
            </blockquote>
            
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-brand">
                <Icon name="Handshake" size={16} />
                <span className="text-sm font-medium">Strategic Partnership</span>
              </div>
              <div className="flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-brand">
                <Icon name="TrendingUp" size={16} />
                <span className="text-sm font-medium">Sustainable Growth</span>
              </div>
              <div className="flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-brand">
                <Icon name="Globe" size={16} />
                <span className="text-sm font-medium">Global Impact</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;